import { Navigate } from 'react-router';
import { describeApiError } from '@/api/errorMessages';
import { useStories } from '@/api/queries';
import { useT } from '@/i18n/context';
import { Page } from '@/ui/Page';

/**
 * `/` — a redirect and nothing else.
 *
 * This was a placeholder, then a redirect to the newest story. The story shelf takes
 * the rest of it away: the shelf, the budget and the create control moved to `/stories`, and
 * what is left is the one job this path has — **get a returning player back into their story
 * without a click**, and get everybody else to the hub.
 *
 * One rule changed with the move, and it is a behaviour change worth naming: it resumes the
 * newest **active** story, where before it resumed the newest non-deleted one. A player whose
 * last run ended in a death was previously dropped into a read-only story with an epitaph and
 * no way forward — correct when `/` was the only page, wrong now that there is a shelf with a
 * "new story" control on it. A dead story is still one click away, from there.
 */
export function HomeRoute() {
  const t = useT();
  const { data: stories, isPending, error } = useStories();

  if (isPending) {
    return (
      <Page title={t('ui.app.title')}>
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {t('ui.story.loading')}
        </p>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title={t('ui.app.title')}>
        <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
          {describeApiError(t, error)}
        </p>
      </Page>
    );
  }

  // Newest first by last activity — `updated_at` moves on every turn, so this is "the story
  // you were playing", not "the story you started most recently".
  const mostRecent = [...stories]
    .filter((story) => story.status === 'active')
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];

  return <Navigate to={mostRecent ? `/play/${mostRecent.id}` : '/stories'} replace />;
}
